import { useState, useRef, useEffect, useCallback, useMemo, CSSProperties } from "react";
import { useFormContext } from "react-hook-form";
import { Badge } from "@/components/ui/badge";
import { X, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { useClinicalTerms } from "@/lib/clinicalTerms";
import TermSuggestion from "./TermSuggestion";
import { TemplateMenu, TemplateOption, useApplyTemplate, useTemplateMatches, type RxTemplate } from "@/components/Template/RxTemplates";

export default function DiagnosisSection({ specialty = "General Medicine", patientAgeMonths, patientSex }: { specialty?: string; patientAgeMonths?: number | null; patientSex?: string | null }) {
    const { setValue, watch, formState: { errors } } = useFormContext();
    const [inputValue, setInputValue] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);

    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const getDropdownStyle = (): CSSProperties => {
        if (!inputRef.current) return {};
        const rect = inputRef.current.getBoundingClientRect();
        const dropdownHeight = Math.min(8 * 36 + 8, 224);
        const wouldOverflowBottom = rect.bottom + dropdownHeight > window.innerHeight - 20;
        const wouldOverflowRight = rect.left + 280 > window.innerWidth - 20;
        return {
            position: "fixed",
            minWidth: 280,
            zIndex:   9999,
            ...(wouldOverflowRight ? { right: window.innerWidth - rect.right } : { left: rect.left }),
            ...(wouldOverflowBottom
                ? { bottom: window.innerHeight - rect.top + 4, top: "auto" }
                : { top: rect.bottom + 2 }),
        };
    };

    const formValue: string = watch("diagnosis") || "";

    const selectedTags = useMemo(() => {
        if (typeof formValue !== "string") return [];
        return formValue.split(",").map((s) => s.trim()).filter(Boolean);
    }, [formValue]);

    const MAX_DIAGNOSIS_LENGTH = 2000;

    const updateFormValue = useCallback((tagsArray: string[]) => {
        const joined = tagsArray.join(", ");
        if (joined.length <= MAX_DIAGNOSIS_LENGTH)
            setValue("diagnosis", joined, { shouldDirty: true });
    }, [setValue]);

    // Empty box: this doctor's most-used diagnoses, then the usual ones for the specialty / patient; typing
    // searches the whole list, abbreviations included ("htn" → Essential Hypertension)
    const diagnosisTerms = useClinicalTerms("diagnosis");
    const matches = useMemo(
        () => diagnosisTerms.suggest(inputValue, { sex: patientSex, ageMonths: patientAgeMonths }, specialty, new Set(selectedTags.map((t) => t.toLowerCase()))),
        [diagnosisTerms, inputValue, patientSex, patientAgeMonths, specialty, selectedTags]
    );
    const filteredSuggestions = matches.map((m) => m.term.name);

    useEffect(() => {
        setFocusedIndex(showSuggestions && filteredSuggestions.length > 0 ? 0 : -1);
    }, [showSuggestions, filteredSuggestions.length]);

    const addTag = useCallback((diagnosis: string) => {
        const trimmed = diagnosis.trim();
        if (trimmed && !selectedTags.includes(trimmed)) {
            updateFormValue([...selectedTags, trimmed]);
        }
        setInputValue("");
        setTimeout(() => {
            inputRef.current?.focus();
            setShowSuggestions(true);
        }, 0);
    }, [selectedTags, updateFormValue]);

    const removeTag = useCallback((name: string) => {
        updateFormValue(selectedTags.filter((t) => t !== name));
    }, [selectedTags, updateFormValue]);

    // Saved templates whose name is being typed — listed after the suggestions, reached with the arrows
    const templates = useTemplateMatches("diagnosis", inputValue);
    const applyTemplate = useApplyTemplate();
    const pickTemplate = (template: RxTemplate) => {
        applyTemplate?.(template);
        setInputValue("");
        setShowSuggestions(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const max = filteredSuggestions.length + templates.length - 1;
        switch (e.key) {
            case "ArrowDown": e.preventDefault(); setFocusedIndex((p) => (p < max ? p + 1 : 0)); break;
            case "ArrowUp": e.preventDefault(); setFocusedIndex((p) => (p > 0 ? p - 1 : max)); break;
            case "Enter":
            case ",":
                e.preventDefault();
                if (focusedIndex >= 0 && focusedIndex < filteredSuggestions.length) {
                    addTag(filteredSuggestions[focusedIndex]);
                } else if (focusedIndex >= filteredSuggestions.length && focusedIndex <= max) {
                    pickTemplate(templates[focusedIndex - filteredSuggestions.length]);
                } else if (inputValue.trim()) {
                    addTag(inputValue.trim());
                }
                break;
            case "Escape": setShowSuggestions(false); break;
            case "Backspace":
                if (inputValue === "" && selectedTags.length > 0) {
                    e.preventDefault();
                    updateFormValue(selectedTags.slice(0, -1));
                }
                break;
            case "Tab": setShowSuggestions(false); break;
        }
    };

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) setShowSuggestions(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    return (
        <div className="space-y-2" ref={containerRef}>
            <div
                className={cn(
                    "min-h-9 touch:min-h-10 px-1.5 py-[3px] border border-field rounded-md cursor-text transition-all bg-card flex items-center",
                    "hover:border-ring/60",
                    showSuggestions && "ring-2 ring-ring/20 border-ring",
                    errors.diagnosis && "border-destructive/30 bg-destructive/5"
                )}
                onClick={() => { inputRef.current?.focus(); setShowSuggestions(true); }}
            >
                <div className="flex flex-wrap items-center gap-1.5 w-full">
                    {selectedTags.map((name, idx) => (
                        <Badge key={`${name}-${idx}`} variant="secondary" className="h-[26px] touch:h-8 px-2 rounded text-[13px] font-semibold bg-primary/10 text-primary border-0 hover:bg-primary/10">
                            <span>{name}</span>
                            <button type="button" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); removeTag(name); }} className="touch-target ml-1.5 p-1 -m-1 hover:text-destructive">
                                <X className="w-3 h-3" />
                            </button>
                        </Badge>
                    ))}
                    <input
                        ref={inputRef} type="text" value={inputValue}
                        onChange={(e) => { setInputValue(e.target.value); setShowSuggestions(true); }}
                        onFocus={() => setShowSuggestions(true)} onKeyDown={handleKeyDown}
                        placeholder={selectedTags.length === 0 ? "Add diagnosis…" : "Add another…"}
                        className="flex-1 min-w-[160px] h-7 touch:h-9 px-1 bg-transparent border-0 outline-none text-sm text-foreground placeholder:text-muted-foreground/60 placeholder:font-normal"
                    />
                    <TemplateMenu type="diagnosis" />
                </div>
            </div>

            {showSuggestions && (filteredSuggestions.length > 0 || inputValue.trim()) && (
                <div
                    className="bg-popover border border-border rounded-lg shadow-lg max-h-56 overflow-y-auto"
                    style={getDropdownStyle()}
                >
                    {matches.map((m, index) => (
                        <TermSuggestion
                            key={m.term.id}
                            match={m}
                            active={index === focusedIndex}
                            onPick={() => addTag(m.term.name)}
                            onForget={() => diagnosisTerms.forget(m.term.id)}
                        />
                    ))}
                    {templates.map((t, i) => (
                        <TemplateOption key={t.id} template={t} active={filteredSuggestions.length + i === focusedIndex} onPick={() => pickTemplate(t)} />
                    ))}
                    {inputValue.trim() && !filteredSuggestions.some(t => t.toLowerCase() === inputValue.toLowerCase().trim()) && !diagnosisTerms.find(inputValue) && (
                        <button
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); addTag(inputValue.trim()); }}
                            className="w-full text-left px-3 py-2 text-sm border-t border-border/40 flex items-center justify-between text-muted-foreground hover:bg-accent"
                        >
                            <span>Add: "{inputValue.trim()}"</span>
                            <Plus className="w-3 h-3 text-[hsl(var(--success))]" />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}